import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ExternalLink } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import RecordMetadata from '@/components/RecordMetadata';
import { vendorCreateSchema, VendorCreate, type AuditProfile } from '@prolarus/shared';

export interface VendorFormData {
  id?: string;
  name: string;
  city: string;
  phone: string;
  email: string;
  address: string;
  state: string;
  country: string;
  created?: string | null;
  modified?: string | null;
  created_by_profile?: AuditProfile | null;
  updated_by_profile?: AuditProfile | null;
}

interface VendorFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendor?: VendorFormData | null;
  onSave: (data: VendorFormData) => Promise<void>;
}

const defaultValues: VendorCreate = {
  name: '',
  city: null,
  phone: null,
  email: null,
  address: null,
  state: null,
  country: 'United States',
};

export default function VendorFormDialog({
  open,
  onOpenChange,
  vendor,
  onSave,
}: VendorFormDialogProps) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<VendorCreate>({
    resolver: zodResolver(vendorCreateSchema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      // Reset form with vendor data or defaults
      if (vendor) {
        form.reset({
          name: vendor.name || '',
          address: vendor.address || null,
          city: vendor.city || null,
          state: vendor.state || null,
          country: vendor.country || null,
          phone: vendor.phone || null,
          email: vendor.email || null,
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, vendor, form]);

  const handleSubmit = async (data: VendorCreate) => {
    setIsLoading(true);
    try {
      // Convert to VendorFormData format for the onSave callback
      const formData: VendorFormData = {
        id: vendor?.id,
        name: data.name,
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        country: data.country || '',
        phone: data.phone || '',
        email: data.email || '',
        created: vendor?.created,
        modified: vendor?.modified,
        created_by_profile: vendor?.created_by_profile,
        updated_by_profile: vendor?.updated_by_profile,
      };
      await onSave(formData);
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  const isEditing = !!vendor?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendor Name *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} type="email" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between gap-3 pt-4">
              <div>
                {isEditing && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      onOpenChange(false);
                      navigate(`/vendors/${vendor?.id}`);
                    }}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Vendor Details
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>

            {isEditing && (
              <RecordMetadata record={vendor} />
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
